import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PenSquare, MailPlus, ShieldCheck, ArrowLeft, CheckCircle2 } from 'lucide-react';

// Shown when detection can't confidently match the file to any parser we've
// built yet. Two calm options: type the details manually, or ask the team
// to build a reader. We NEVER guess at numbers to protect trust.
const InstitutionImportUnrecognised = ({
  unmatched, requestSaving,
  onEditName, onEnterManually, onRequestReader, onBack, stage,
}) => {
  if (stage === 'requested') {
    return (
      <div className="text-center py-10 space-y-3" data-testid="institution-import-requested">
        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-700" />
        </div>
        <h3 className="text-lg font-serif text-slate-900">Thanks — we&rsquo;ve noted your institution.</h3>
        <p className="text-sm text-slate-600 max-w-md mx-auto">
          You&rsquo;ll get an email the moment we&rsquo;re ready to read files from{' '}
          <span className="font-medium">{unmatched?.editableName}</span>. In the meantime,
          you can still enter details manually to keep your records up to date.
        </p>
        <Button onClick={onEnterManually} className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="institution-import-requested-manual">
          Enter details manually
        </Button>
      </div>
    );
  }

  const guessLabel = unmatched?.editableName || 'this institution';
  const isRecognised = !!unmatched?.recognised;
  const heading = isRecognised
    ? `We recognise ${guessLabel} — the reader is being built.`
    : `We don't have a reader for ${guessLabel} yet.`;
  const subtext = isRecognised
    ? "Good news — your institution is one of the first ten we're building readers for. We haven't finished the number-reading logic yet, so we won't guess at your holdings. In the meantime you have two options:"
    : "We won't guess at your numbers — that's how records get wrong. Your file stayed on our server just long enough to check its shape and was never opened row-by-row.";

  return (
    <div className="space-y-5" data-testid="institution-import-unrecognised">
      <div>
        <h3 className="text-lg font-serif text-slate-900" data-testid="institution-import-unrecognised-heading">
          {heading}
        </h3>
        <p className="text-sm text-slate-600 mt-2">{subtext}</p>
      </div>

      {!isRecognised && (
        <div className="rounded-lg border border-slate-200 p-4 bg-white space-y-2">
          <label className="text-xs font-medium text-slate-700">Institution name</label>
          <Input
            value={unmatched?.editableName || ''}
            onChange={(e) => onEditName(e.target.value)}
            placeholder="e.g. ARM Investment Managers"
            data-testid="institution-import-name-input"
          />
          <p className="text-xs text-slate-500">
            We took a guess from the filename — please tidy it if it&rsquo;s off.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          type="button" onClick={onEnterManually}
          className="text-left rounded-lg border border-slate-200 p-4 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors"
          data-testid="institution-import-manual-btn"
        >
          <div className="flex items-start gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg"><PenSquare className="w-5 h-5 text-emerald-700" /></div>
            <div>
              <p className="font-semibold text-slate-900">Enter the details manually</p>
              <p className="text-xs text-slate-600 mt-1">
                Opens the guided form. Usually under a minute per holding, and every field is checked as you type.
              </p>
            </div>
          </div>
        </button>
        <button
          type="button" onClick={onRequestReader} disabled={requestSaving}
          className="text-left rounded-lg border border-slate-200 p-4 hover:border-amber-300 hover:bg-amber-50/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="institution-import-request-btn"
        >
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-100 rounded-lg"><MailPlus className="w-5 h-5 text-amber-700" /></div>
            <div>
              <p className="font-semibold text-slate-900">
                {requestSaving
                  ? 'Sending your request…'
                  : isRecognised
                    ? `Email me the moment the ${guessLabel} reader ships`
                    : `Ask our team to build a reader for ${guessLabel}`}
              </p>
              <p className="text-xs text-slate-600 mt-1">
                {isRecognised
                  ? `You'll be first in line. We only email you once — when the ${guessLabel} reader is ready.`
                  : `We'll add ${guessLabel} to our next batch and email you the moment it's ready.`}
              </p>
            </div>
          </div>
        </button>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500 pt-1">
        <div className="flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
          <span>Your file is safe with us and untouched.</span>
        </div>
        <button type="button" onClick={onBack} className="hover:text-slate-700 flex items-center gap-1"
          data-testid="institution-import-unrecognised-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Choose a different file
        </button>
      </div>
    </div>
  );
};

export default InstitutionImportUnrecognised;
